import * as React from 'react';
import { observer } from 'mobx-react';
import * as dateFns from 'date-fns';
import { Select } from '@payscale/design';
import { PayHistoryChangeEntry } from 'nomst/Stores/Decision/payHistoryChangeEntry';
import { PayHistory } from 'nomst/Stores/Decision/payHistory';
import { Format } from 'nomst/Stores/Utility/format';
import { PayHistoryChangeEntryView } from './payHistoryChangeEntryView';
import { PayHistoryBarChartCard, PayHistoryDataPoint }
    from '@payscale/payscale-shared-ui/lib/EmployeeDetailsCards/PayHistoryBarChartCard';
import { dateFormatYearOnly } from 'common/date-operation';
import { AmountCode } from 'typings/enums';
import './payHistoryView.scss';
import { currencyConversion } from 'utils/MobxDataManager/singletons';

export interface IPayHistoryViewProps {
    payHistory: Nullable<PayHistory>;
    format: Format;
    selectedCurrency?: string;
}

export interface IPayHistoryViewState {
    payHistoryFilter: string;
}

const payHistoryFilterOptions: Array<ISelectOption<string>> = [
    { value: '', text: 'All pay changes' },
    { value: AmountCode.Merit, text: 'Merit' },
    { value: AmountCode.Bonus, text: 'Bonus' },
    { value: AmountCode.MarketAdjustment, text: 'Market Adjustment' },
    { value: AmountCode.LumpSum, text: 'Lump Sum' },
    { value: AmountCode.Promotion, text: 'Promotion' },
    { value: AmountCode.Stock, text: 'Stock' },
    { value: AmountCode.VariablePay, text: 'Variable Pay' }
];

export class PayHistoryView extends React.Component<IPayHistoryViewProps, IPayHistoryViewState> {
    constructor(props: IPayHistoryViewProps) {
        super(props);
        this.state = { payHistoryFilter: '' };

        this.handlePayHistoryFilterChange = this.handlePayHistoryFilterChange.bind(this);
    }

    render(): React.ReactNode {
        const { payHistory, selectedCurrency } = this.props;
        const { payHistoryFilter } = this.state;

        if (!payHistory || !payHistory.payHistoryChangeEntryYears.length) {
            return (
                <div className="nomst-pay-history nomst-pay-history--empty">
                    <img
                        alt=""
                        height="150"
                        width="150"
                        src={require('contents/change_history.svg')}
                        className="nomst-pay-history__change-history-icon"
                    />
                    <div className="nomst-pay-history__text">
                        No pay history available
                    </div>
                </ div>
            );
        }
        let payHistoryChanges: PayHistoryChangeEntry[] = payHistory.payHistoryChangeEntries;

        if (payHistoryFilter) {
            // filter the pay history changes to remove any that aren't the current filtered pay change type
            payHistoryChanges = payHistory.filteredSortedPayHistoryEntries(payHistoryFilter);
        }

        // prefer TCC to base pay for chart data, as it shows more of variety.
        const tccPayHistoryDataPoints = payHistoryChanges.map(item => ({
            baseSalary: this.getConvertedValue(
                item.tcc,
                item.isoCurrencyCode),
            createdAt: item.payEffectiveDate
        }));

        // create basePay fallback just in case TCC doesn't exist
        const basePayHistoryDataPoints = payHistoryChanges.map(item => ({
            baseSalary: this.getConvertedValue(
                item.basePay,
                item.isoCurrencyCode),
            createdAt: item.payEffectiveDate
        }));

        const mostRecentPayEventCurrency = payHistoryChanges[payHistoryChanges.length -1]?.isoCurrencyCode;
        let mostRecentPayEvent: PayHistoryDataPoint = tccPayHistoryDataPoints[tccPayHistoryDataPoints.length - 1];
        let payHistoryChart: React.ReactNode = null;
        const currency = selectedCurrency || mostRecentPayEventCurrency;

        // show chart if there's at least 2 data points
        if (tccPayHistoryDataPoints.length > 1 && tccPayHistoryDataPoints[0]?.baseSalary > 0) {
            payHistoryChart = (
                <PayHistoryBarChartCard
                    currency={currency ?? 'USD'}
                    baseSalary={mostRecentPayEvent.baseSalary}
                    historyDataPoints={tccPayHistoryDataPoints}
                    payEffectiveDate={mostRecentPayEvent.createdAt}
                />
            );
        } else if (basePayHistoryDataPoints.length > 1) {
            // fallback to base pay if customer does not have TCC turned on
            mostRecentPayEvent = basePayHistoryDataPoints[basePayHistoryDataPoints.length - 1];
            payHistoryChart = (
                <PayHistoryBarChartCard
                    currency={currency ?? 'USD'}
                    baseSalary={mostRecentPayEvent.baseSalary}
                    historyDataPoints={basePayHistoryDataPoints}
                    payEffectiveDate={mostRecentPayEvent.createdAt}
                />
            );
        }

        return (
            <div className="nomst-pay-history">
                <Select
                    id="filterPayHistory"
                    className="pay-history-filter"
                    value={this.state.payHistoryFilter}
                    onChange={this.handlePayHistoryFilterChange}
                    options={payHistoryFilterOptions}
                />

                {payHistoryChart}

                <div className="ph-headers row">
                    <div className="ph-headers__effective-date col-xs-3 col-md-3">Effective Date</div>
                    <div className="col-xs-9 col-md-9">Pay Changes</div>
                </div>
                {payHistory.payHistoryChangeEntryYears
                    .map(year => this.renderPayHistoryEntriesOfTheYear(payHistory, year))}
                {this.renderPayHistoryHiredOnEntry(payHistory)}
            </div>
        );
    }

    private handlePayHistoryFilterChange(_: undefined, value: string): void {
        this.setState({ payHistoryFilter: value });
    }

    private renderPayHistoryEntriesOfTheYear(payHistory: PayHistory, year: number): React.ReactNode {
        const payHistoryChangeEntriesOfTheYear
            = payHistory.sortedPayHistoryEntriesOfTheYear(year, this.state.payHistoryFilter);
        if (!payHistoryChangeEntriesOfTheYear) {
            return null;
        }

        const payHistoryChangeEntryExists = (payHistoryChangeEntriesOfTheYear.length > 0);
        const filterExists = (this.state.payHistoryFilter.length > 0);
        if (!payHistoryChangeEntryExists && filterExists) {
            return null;
        }

        if (!payHistoryChangeEntryExists) {
            return (
                <PayHistoryChangeEntryView
                    shouldRenderYear={true}
                    year={year}
                    text={this.noPayChangesText(year)}
                    key={year}
                    format={this.props.format}
                    selectedCurrency={this.props.selectedCurrency}
                />
            );
        }

        return payHistoryChangeEntriesOfTheYear.map(
            (payHistoryChangeEntry, index) => (
                <PayHistoryChangeEntryView
                    shouldRenderYear={!index}
                    payHistory={payHistory}
                    payHistoryChangeEntry={payHistoryChangeEntry}
                    key={`${year}-${payHistoryChangeEntry.payHistoryChangeEntryId}`}
                    format={this.props.format}
                    selectedCurrency={this.props.selectedCurrency}
                />
            ));
    }

    private renderPayHistoryHiredOnEntry(payHistory: PayHistory): React.ReactNode {
        const filterExists = !!this.state.payHistoryFilter.length;
        const hireDateYearText = this.hireDateYearText();
        if (filterExists || !payHistory.isHiredOnEntryValid() || !hireDateYearText) {
            return null;
        }

        return (
            <PayHistoryChangeEntryView
                shouldRenderYear={this.shouldRenderHiredOnYear()}
                payHistory={payHistory}
                key={hireDateYearText}
                format={this.props.format}
                selectedCurrency={this.props.selectedCurrency}
            />
        );
    }

    private noPayChangesText(year: number): string {
        const currentYear = new Date().getFullYear();
        return (currentYear === year) ? 'No pay changes yet' : 'No pay changes';
    }

    private hireDateYearText(): Nullable<string> {
        const { payHistory } = this.props;
        return (payHistory && payHistory.hireDate) ? dateFormatYearOnly(payHistory.hireDate) : null;
    }

    private shouldRenderHiredOnYear(): boolean {
        const { payHistory } = this.props;

        return !!payHistory && !payHistory.payHistoryChangeEntries.some(
            payHistoryChangeEntry =>
                dateFns.isSameYear(payHistoryChangeEntry.payEffectiveDateType, new Date(payHistory.hireDateType || '')));
    }

    private getConvertedValue(value: number, sourceCurrency: string): number {
        const { selectedCurrency } = this.props;
        let convertedValue = value;
        if (selectedCurrency) {
            convertedValue = currencyConversion.convertCurrency(sourceCurrency, selectedCurrency, value);
        }
        return convertedValue;
    }
}

export default observer(PayHistoryView);